import { RaidPlanEncounterAssignmentAbility } from 'app/frontend/types/custom/RaidPlan'
import { EncounterAbilityRow } from '../../TeamPlans/hooks/useTeamPlanComputedData'
import {
  AssignmentViewMode,
  getEffectiveAbilityCustomGrouping,
  getEffectiveAssignmentGroups,
  getEffectiveAssignmentViewMode,
  getAssignmentScopeIndexes,
  normalizeAssignmentBox,
  normalizeAssignmentCast,
} from '../../TeamPlans/components/assignmentTimeline'
import {
  EncounterAssignmentEntityOverride,
  EventPlanEncounterAssignmentOverrides,
  getEncounterAssignmentEntityOverride,
} from '../components/eventActivityPlanOverrides'
import {
  EventActivityTimelineResolverIssue,
  ResolvedAssignmentEntity,
  ResolvedAssignmentParticipant,
} from '../components/eventActivityTimelineResolver'

type ApplyEventActivityPlanOverridesInput = {
  resolvedByScopeBoxKey: Record<string, ResolvedAssignmentEntity[]>
  abilities: RaidPlanEncounterAssignmentAbility[]
  encounterRows: EncounterAbilityRow[]
  selectedParticipants: ResolvedAssignmentParticipant[]
  encounterAssignmentOverrides?: EventPlanEncounterAssignmentOverrides
}

const toCastIds = (
  ability: RaidPlanEncounterAssignmentAbility,
  castIndexes: number[],
  assignmentGroups: number,
) =>
  castIndexes
    .map(
      (castIndex) =>
        normalizeAssignmentCast(ability.casts?.[castIndex], assignmentGroups)
          .id,
    )
    .filter((castId): castId is string => Boolean(castId))

export const applyEventActivityPlanOverrides = ({
  resolvedByScopeBoxKey,
  abilities,
  encounterRows,
  selectedParticipants,
  encounterAssignmentOverrides,
}: ApplyEventActivityPlanOverridesInput) => {
  if (!encounterAssignmentOverrides) return resolvedByScopeBoxKey
  const selectedParticipantByCharacterId = selectedParticipants.reduce<
    Record<number, ResolvedAssignmentParticipant>
  >((acc, participant) => {
    acc[participant.characterId] = participant
    return acc
  }, {})
  const nextResolvedByScopeBoxKey = { ...resolvedByScopeBoxKey }

  abilities.forEach((ability, abilityIndex) => {
    const row = encounterRows[abilityIndex]
    if (!row) return
    const assignmentGroups = getEffectiveAssignmentGroups(
      row.assignmentGroups,
      ability.assignment_groups,
    )
    const viewMode: AssignmentViewMode = getEffectiveAssignmentViewMode(
      ability.view_mode,
      row.entries.length,
    )
    const customGrouping = getEffectiveAbilityCustomGrouping(
      ability.spell_id,
      ability.custom_grouping,
    )
    const scopes = getAssignmentScopeIndexes(
      row.entries,
      viewMode,
      customGrouping,
      ability.assignment_type,
    )

    scopes.forEach((scope) => {
      const castIds = toCastIds(ability, scope.castIndexes, assignmentGroups)
      if (!castIds.length) return
      const representativeCast = normalizeAssignmentCast(
        ability.casts?.[scope.castIndexes[0]],
        assignmentGroups,
      )

      for (let boxIndex = 0; boxIndex < assignmentGroups; boxIndex += 1) {
        const key = `${row.abilityKey}:${scope.id}:${boxIndex}`
        const resolvedEntities = nextResolvedByScopeBoxKey[key]
        if (!resolvedEntities?.length) continue
        const box = normalizeAssignmentBox(representativeCast.boxes[boxIndex])

        nextResolvedByScopeBoxKey[key] = resolvedEntities.map(
          (resolvedEntity, entityIndex) => {
            const sourceEntity =
              box.entities[entityIndex] ?? resolvedEntity.sourceEntity
            const override = getEncounterAssignmentEntityOverride({
              encounterAssignments: encounterAssignmentOverrides,
              abilityKey: row.abilityKey,
              castIds,
              boxId: box.id,
              entityId: sourceEntity?.id,
            })
            if (!override) return resolvedEntity
            if (override.kind === 'participant') {
              const participant =
                selectedParticipantByCharacterId[override.characterId]
              if (!participant) return resolvedEntity
              return {
                kind: 'participant',
                participant,
                sourceEntity: resolvedEntity.sourceEntity,
                isOverridden: true,
              }
            }
            return toAggregateOverrideEntity(override)
          },
        )
      }
    })
  })

  return nextResolvedByScopeBoxKey
}

const toAggregateOverrideEntity = (
  override: Extract<EncounterAssignmentEntityOverride, { kind: 'aggregate' }>,
): ResolvedAssignmentEntity => ({
  kind: 'aggregate',
  sourceEntity: override.entity,
  isOverridden: true,
})

const scopeBoxKeyMatchesAbility = (scopeBoxKey: string, abilityKey: string) =>
  scopeBoxKey === abilityKey || scopeBoxKey.startsWith(`${abilityKey}:`)

export const getEventActivityUnresolvedIssues = (
  resolvedByScopeBoxKey: Record<string, ResolvedAssignmentEntity[]>,
  hiddenAbilityKeys?: Iterable<string>,
): EventActivityTimelineResolverIssue[] => {
  const hiddenKeys = hiddenAbilityKeys ? new Set(hiddenAbilityKeys) : null
  const issues: EventActivityTimelineResolverIssue[] = []
  Object.entries(resolvedByScopeBoxKey).forEach(
    ([scopeBoxKey, resolvedEntities]) => {
      if (
        hiddenKeys &&
        Array.from(hiddenKeys).some((abilityKey) =>
          scopeBoxKeyMatchesAbility(scopeBoxKey, abilityKey),
        )
      ) {
        return
      }
      resolvedEntities.forEach((entity) => {
        if (entity.issue) issues.push(entity.issue)
      })
    },
  )
  return issues
}
